import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
} from 'react';

import api from '../services/apiClient';

import { useToast } from './toast';

import Users from '../interfaces/IUsers';
import IUserSubordinado from '../interfaces/IUserSubordinado';

interface UserStatusContextData {
  userStatus: Users[];
  loadAllUsersStatus: () => void;
  setUserStatus: (userStatus: Users[]) => void;
  updateUserStatus: (userStatus: Users) => void;
  disconnectUser: (seqUsuario: number) => void;
  loadUserSubordinado: () => Promise<void>;
  userSubordinado: IUserSubordinado[];
  loadAllUser: () => void;
  allUser: Users[];
}

const UserStatusContext = createContext<UserStatusContextData>(
  {} as UserStatusContextData
);

function useUserStatus(): UserStatusContextData {
  const context = useContext(UserStatusContext);
  if (!context) {
    throw new Error('useUserStatus must be used within an AuthProvider');
  }
  return context;
}

const UserStatusProvider: React.FC = ({ children }): any => {
  const [userStatus, setUserStatus] = React.useState<Users[]>([] as Users[]);
  const [userSubordinado, setUserSubordinado] = useState<IUserSubordinado[]>(
    []
  );
  const [allUser, setAllUser] = useState<Users[]>([] as Users[]);
  const { addToast } = useToast();

  const loadAllUser = (): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('/api/user/allUsers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;
        if (result) {
          setAllUser(result.data);
        }
      });
  };

  const loadAllUsersStatus = useCallback((): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .get('grupoUsuario/allUsers', {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success) {
          setUserStatus(response.data.data);
        }
      });
  }, []);

  const loadUserSubordinado = async (): Promise<void> => {
    const token = localStorage.getItem('@LiveChat:token');

    const response = await api.get(`/Supervisor`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    setUserSubordinado(response.data.data);
  };

  const disconnectUser = useCallback((seqUsuario: number): void => {
    const token = localStorage.getItem('@LiveChat:token');

    api
      .post(`Login/LogoutUser/${seqUsuario}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        if (response.data.success) {
          addToast({
            type: 'success',
            title: 'Solicitação enviada',
            description: 'Usuário desconectado !',
          });
        }
      });
  }, []);

  const updateUserStatus = useCallback((newUserStatus: Users): void => {
    const userLogado = localStorage
      .getItem('@LiveChat:user')
      ?.toLocaleUpperCase();

    if (userLogado === newUserStatus.user) return;

    setUserStatus((user) => [
      ...user.filter((usr) => usr.user !== newUserStatus.user),
      newUserStatus,
    ]);
  }, []);

  const valueContext = useMemo(
    () => ({
      userStatus,
      setUserStatus,
      loadAllUsersStatus,
      updateUserStatus,
      disconnectUser,
      loadUserSubordinado,
      userSubordinado,
      loadAllUser,
      allUser,
    }),
    [userStatus, loadAllUsersStatus]
  );

  return (
    <UserStatusContext.Provider value={valueContext}>
      {children}
    </UserStatusContext.Provider>
  );
};

export { UserStatusProvider, useUserStatus };
